// IMPORTANT NOTE:
// We surface the app version without importing package.json (blocked outside src in CRA)
// and without changing other critical files. We first read REACT_APP_VERSION (if provided
// at build time). As a fallback, we fetch /service-worker.js at runtime and parse the
// version embedded in its cache keys. This keeps the change localized to this component
// and avoids touching broader build/config files.
import React, { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ClavaContext, ClavaRootContext } from "../../../config/contexts";
import { translate } from "../../../config/translator";
import { isContentManager } from "../../../config/utils";
import { pathHome, pathTeamManagement } from "../../../config/routes";
import logoPng from "../../../assets/images/logo-app.png";
import logoPngWhite from "../../../assets/images/logo-app-white.png";
import appStoreBadgePng from "../../../assets/images/appleAppStoreBadge.png";
import playStoreBadgePng from "../../../assets/images/googlePlayBadge.png";
// Read from env if provided at build time (CRA exposes only REACT_APP_*).
const ENV_VERSION = process.env.REACT_APP_VERSION;

const Footer: React.FC = () => {
  const clava = useContext(ClavaContext);
  const { theme } = useContext(ClavaRootContext);
  const user = clava?.user;
  const l = clava?.l ?? "en";

  const [version, setVersion] = useState<string>(ENV_VERSION ?? "");

  useEffect(() => {
    if (version) return;
    // Fallback: parse version from the public service worker file where it's embedded in cache keys
    fetch("/service-worker.js", { cache: "no-store" })
      .then((r) => (r.ok ? r.text() : ""))
      .then((text) => {
        if (!text) return;
        const match = text.match(/v(\d+\.\d+\.\d+)/);
        if (match && match[1]) setVersion(match[1]);
      })
      .catch(() => {
        // ignore
      });
  }, [version]);

  return (
    <footer className="w-full border-t border-light-gray/40 dark:border-light-gray/20 mt-6 pt-12 pb-24 px-20 xl:px-40 bg-bg dark:bg-bg-dark text-xs text-muted dark:text-muted-dark">
      <div className="w-full max-w-screen-xl mx-auto flex flex-row flex-wrap items-start justify-start gap-16">
        <div className="flex flex-col items-start gap-2">
        <NavLink to={pathHome()} className="ml-0">
          <img
            className="h-12 w-auto xl:h-14 object-contain shrink-0 -mt-3"
            src={theme === "dark" ? logoPng : logoPngWhite}
            alt={translate("emptyString", l)}
            title={translate("emptyString", l)}
          />
        </NavLink>
        <span className="text-xs text-font-600 dark:text-font-600-dark">&copy; Copyright 2025, Clava Sports</span>
          {version && isContentManager(user) ? (
            <span className="font-light text-xs ml-2">v{version}</span>
          ) : null}
        </div>
        <nav className="grid grid-cols-2 gap-16 xl:grid-cols-4 xl:gap-32 text-xs w-full">
        <div className="flex flex-col gap-2">
          <h3 className="text-sm  text-font-600 dark:text-font-600-dark">{translate("company", l)}</h3>
          <a className="underline" href="https://info.clava-sports.com" target="_blank" rel="noreferrer">
            {translate("aboutUs", l)}
          </a>
          <a className="underline" href="https://info.clava-sports.com/werben-auf-clava" target="_blank" rel="noreferrer">
            {translate("ads", l)}
          </a>
          <a className="underline" href="https://info.clava-sports.com/kontakt" target="_blank" rel="noreferrer">
            {translate("contactUs", l)}
          </a>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-sm  text-font-600 dark:text-font-600-dark">{translate("legal", l)}</h3>
          <NavLink to="/imprint" className="underline">
            {translate("imprint", l)}
          </NavLink>
          <a className="underline" href="https://clav.app/app/tos" target="_blank" rel="noreferrer">
            {translate("tos", l)}
          </a>
          <a className="underline" href="https://clav.app/app/privacy" target="_blank" rel="noreferrer">
            {translate("privacy", l)}
          </a>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-sm  text-font-600 dark:text-font-600-dark">{translate("usefulLinks", l)}</h3>
          <a className="underline" href="https://info.clava-sports.com/warum-clava" target="_blank" rel="noreferrer">
            {translate("whyClava", l)}
          </a>
          <a className="underline" href="https://info.clava-sports.com/faq-center" target="_blank" rel="noreferrer">
            {translate("faq", l)}
          </a>
          {user?.groups && user.groups.length > 0 ? (
            <NavLink to={pathTeamManagement()} className="underline">
              {translate("teamManagement", l)}
            </NavLink>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 min-w-[8rem]">
          <h3 className="text-sm  text-font-600 dark:text-font-600-dark">{translate("downloadAppTitle", l)}</h3>
          <div className="flex flex-col items-start gap-3">
            <a href="https://apps.apple.com/us/app/clava-live-results/id1637227932" target="_blank" rel="noreferrer" aria-label="Download on the App Store" className="inline-flex">
              <img src={appStoreBadgePng} alt="App Store" className="h-10 w-auto max-w-full object-contain shrink-0" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.clava" target="_blank" rel="noreferrer" aria-label="Get it on Google Play" className="inline-flex">
              <img src={playStoreBadgePng} alt="Google Play" className="h-10 w-auto max-w-full object-contain shrink-0" />
            </a>
          </div>
        </div>
        </nav>
      </div>
    </footer>
  );
};

export default Footer;


